import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { 
  Signal, RefreshCw, Flame, Target, Timer, BarChart3, Zap, Bell, Send, Rocket
} from 'lucide-react';

// Import refactored components
import SignalCard from '../components/signals/ui/SignalCard';
import HistoryTab from '../components/signals/tabs/HistoryTab';
import PositionsTab from '../components/signals/tabs/PositionsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Premium CTA Banner - Only for free users
const PremiumCTABanner = ({ onUpgrade }) => (
  <div className="mt-8 relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/40 via-slate-900/90 to-slate-950">
    {/* Subtle pattern overlay */}
    <div className="absolute inset-0 opacity-5">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0,229,153,0.3) 1px, transparent 0)`,
        backgroundSize: '24px 24px'
      }} />
    </div>
    
    <div className="relative px-6 py-8 md:px-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        {/* Left Side - Text Content */}
        <div className="flex-1">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Zap className="h-6 w-6 text-amber-400" />
            Ekran Başında Nöbet Tutma!
          </h3>
          <p className="text-slate-300 text-sm md:text-base mb-4 max-w-xl">
            Pro üyeler, sinyaller oluştuğu anda <span className="text-emerald-400 font-medium">Telegram bildirimi</span> alır. 
            Sayfayı yenilemeyi bırak, robot senin yerine takip etsin.
          </p>
          
          {/* Feature Icons */}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 text-slate-300">
              <Send className="h-4 w-4 text-blue-400" />
              Telegram Botu
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-300">
              <Bell className="h-4 w-4 text-amber-400" />
              Anlık Uyarılar
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-300">
              <Rocket className="h-4 w-4 text-purple-400" />
              Öncelikli Erişim
            </span>
          </div>
        </div>
        
        {/* Right Side - CTA Button */}
        <div className="flex-shrink-0">
          <button 
            onClick={onUpgrade}
            style={{
              background: 'linear-gradient(to right, #f59e0b, #d97706)',
              color: '#000000',
              boxShadow: '0 4px 20px -3px rgba(245, 158, 11, 0.4)',
            }}
            className="w-full md:w-auto px-6 py-3 rounded-lg font-bold text-base flex items-center justify-center gap-2 hover:scale-105 transition-transform animate-pulse-soft"
            data-testid="premium-cta-btn"
          >
            <Zap className="h-5 w-5" />
            Anlık Sinyalleri Kaçırma
          </button>
        </div>
      </div>
    </div>
  </div>
);

const SimpleSignalsPage = () => {
  const { user, loading: authLoading, isPro } = useAuth();
  const navigate = useNavigate();
  const [signals, setSignals] = useState([]);
  const [positions, setPositions] = useState([]);
  const [marketStatus, setMarketStatus] = useState(null);
  const [livePrices, setLivePrices] = useState({});
  const [priceChanges, setPriceChanges] = useState({});
  const previousPricesRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  
  // Redirect non-authenticated users to /performans
  // Wait for auth to finish loading before redirecting
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/performans');
    }
  }, [user, authLoading, navigate]);
  
  // Track price changes for animation
  useEffect(() => {
    if (Object.keys(livePrices).length > 0) {
      const changes = {};
      Object.keys(livePrices).forEach(symbol => {
        const prevPrice = previousPricesRef.current[symbol];
        const currentPrice = livePrices[symbol];
        if (prevPrice !== undefined && prevPrice !== currentPrice) {
          changes[symbol] = currentPrice > prevPrice ? 'up' : 'down';
        }
      });
      
      if (Object.keys(changes).length > 0) {
        setPriceChanges(changes);
        setTimeout(() => {
          setPriceChanges({});
        }, 1500);
      }
      
      previousPricesRef.current = { ...livePrices };
    }
  }, [livePrices]);

  const fetchSignals = async () => {
    setRefreshing(true);
    try {
      const token = user ? localStorage.getItem('token') : null;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/signals/live`, { headers });
      const allSignals = response.data?.signals || response.data || [];
      
      // Filter for NEW OPPORTUNITIES (PENDING signals only)
      const pendingSignals = allSignals.filter(s => {
        // Case-insensitive status check
        const status = (s.status || '').toLowerCase();
        if (status !== 'pending') return false;
        if (s.outcome_status === 'stopped' || s.outcome_status === 'success' || s.outcome_status === 'full_success' || s.outcome_status === 'expired') return false;
        if (s.entry_hit) return false;
        
        // Check if signal has expired (expires_at check)
        if (s.expires_at) {
          const expiresAt = new Date(s.expires_at);
          if (expiresAt < new Date()) {
            return false; // Signal expired
          }
        }
        
        return true;
      });
      
      // Sort by timestamp - newest first (en yeniden en eskiye)
      pendingSignals.sort((a, b) => {
        const timeA = new Date(a.timestamp || a.created_at || 0).getTime();
        const timeB = new Date(b.timestamp || b.created_at || 0).getTime();
        return timeB - timeA; // Descending (newest first)
      });
      
      setSignals(pendingSignals);
    } catch (err) {
      console.error('Signals fetch error:', err);
      setSignals([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchMarketStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/signals/market/status`);
      setMarketStatus(response.data);
    } catch (err) {
      console.error('Market status fetch error:', err);
    }
  };

  const fetchPositions = async () => {
    try {
      const token = user ? localStorage.getItem('token') : null;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/signals/positions/active`, { headers });
      return response.data.positions || [];
    } catch (err) {
      console.error('Positions fetch error:', err);
      return [];
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = user ? localStorage.getItem('token') : null;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Optimized: Removed scanner API call (was causing slow load)
        const [positionsRes, signalsRes, marketRes, pricesRes] = await Promise.all([
          axios.get(`${API_URL}/api/signals/positions/active`, { headers }).catch(() => ({ data: { positions: [] } })),
          axios.get(`${API_URL}/api/signals/live`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/signals/market/status`).catch(() => ({ data: null })),
          axios.get(`${API_URL}/api/signals/prices/live`).catch(() => ({ data: { prices: {} } }))
        ]);
        
        const posData = positionsRes.data.positions || [];
        const allSignals = signalsRes.data?.signals || signalsRes.data || [];
        
        const pendingSignals = allSignals.filter(s => {
          const status = (s.status || '').toLowerCase();
          if (status !== 'pending') return false;
          if (s.outcome_status === 'stopped' || s.outcome_status === 'success' || s.outcome_status === 'full_success' || s.outcome_status === 'expired') return false;
          if (s.entry_hit) return false;
          
          // Check if signal has expired (expires_at check)
          if (s.expires_at) {
            const expiresAt = new Date(s.expires_at);
            if (expiresAt < new Date()) {
              return false; // Signal expired
            }
          }
          
          return true;
        });
        
        // Sort by timestamp - newest first (en yeniden en eskiye)
        pendingSignals.sort((a, b) => {
          const timeA = new Date(a.timestamp || a.created_at || 0).getTime();
          const timeB = new Date(b.timestamp || b.created_at || 0).getTime();
          return timeB - timeA; // Descending (newest first)
        });
        
        setPositions(posData);
        setSignals(pendingSignals);
        setMarketStatus(marketRes.data);
        setLivePrices(pricesRes.data?.prices || {});
      } catch (err) {
        console.error('Data fetch error:', err);
        setSignals([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    };
    
    loadData();
    const interval = setInterval(() => {
      fetchSignals();
      fetchMarketStatus();
      fetchPositions().then(setPositions);
    }, 60000);
    
    // Live prices refresh - every 3 seconds (WebSocket provides real-time data)
    const priceInterval = setInterval(async () => {
      try {
        const pricesRes = await axios.get(`${API_URL}/api/signals/prices/live`);
        setLivePrices(pricesRes.data?.prices || {});
      } catch (err) {
        console.error('Live prices refresh error:', err);
      }
    }, 3000);  // 3 seconds for near real-time feel
    
    // Fetch prices immediately on mount
    (async () => {
      try {
        const pricesRes = await axios.get(`${API_URL}/api/signals/prices/live`);
        setLivePrices(pricesRes.data?.prices || {});
      } catch (err) {
        console.error('Initial prices fetch error:', err);
      }
    })();
    
    return () => {
      clearInterval(interval);
      clearInterval(priceInterval);
    };
  }, [user]);

  // Stats
  const buySignals = signals.filter(s => s.action === 'BUY');
  const exitSignals = signals.filter(s => s.action === 'EXIT');
  const todaySignals = signals.filter(s => {
    const signalDate = new Date(s.timestamp || s.created_at);
    const today = new Date();
    return signalDate.toDateString() === today.toDateString();
  });

  // Best signal of the day
  const bestSignal = signals.reduce((best, current) => {
    if (!best) return current;
    const currentScore = (current.confidence || 0) * 100 + (current.pnl_percent || 0);
    const bestScore = (best.confidence || 0) * 100 + (best.pnl_percent || 0);
    return currentScore > bestScore ? current : best;
  }, null);

  // If auth is loading, show loading spinner
  if (authLoading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-brand-green animate-spin" />
      </div>
    );
  }

  // If not authenticated, show loading while redirecting
  if (!user) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-brand-green animate-spin" />
      </div>
    );
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark py-6" data-testid="simple-signals-page">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-800 animate-pulse" />
              <div className="h-8 w-32 bg-slate-800 rounded animate-pulse" />
            </div>
            <div className="h-9 w-24 bg-slate-800 rounded animate-pulse" />
          </div>
          
          <div className="flex gap-1 mb-6 bg-slate-900/50 p-1 rounded-xl">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex-1 h-10 bg-slate-800 rounded-lg animate-pulse" />
            ))}
          </div>
          
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="border border-slate-700/50 rounded-xl p-4 bg-slate-900/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-6 w-24 bg-slate-800 rounded animate-pulse" />
                  <div className="h-5 w-20 bg-slate-800 rounded animate-pulse" />
                </div>
                <div className="h-16 bg-slate-800 rounded animate-pulse mb-3" />
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="h-12 bg-slate-800 rounded animate-pulse" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-dark py-6" data-testid="simple-signals-page">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-green/20 flex items-center justify-center">
                <Signal className="h-5 w-5 text-brand-green" />
              </div>
              Sinyaller
            </h1>
          </div>
          {isPro ? (
            <Button 
              onClick={() => { fetchSignals(); fetchMarketStatus(); }} 
              variant="outline" 
              size="sm"
              disabled={refreshing}
              className="border-slate-700 hover:border-slate-600"
              data-testid="refresh-signals-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Yenile
            </Button>
          ) : (
            <button 
              onClick={() => navigate('/pricing')} 
              style={{
                background: 'linear-gradient(to right, #f59e0b, #d97706)',
                backgroundColor: '#f59e0b',
                color: '#000000',
                boxShadow: '0 4px 15px -3px rgba(245, 158, 11, 0.3)',
                border: 'none',
                outline: 'none',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}
              className="animate-pulse-soft hover:scale-105 transition-transform"
              data-testid="upgrade-cta-btn"
            >
              <Zap className="h-4 w-4" style={{ color: '#000000' }} />
              Sinyalleri Kaçırma
            </button>
          )}
        </div>

        {/* 3-Tab Navigation */}
        <div className="flex gap-1 mb-6 bg-slate-900/50 p-1 rounded-xl overflow-x-auto">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'active' 
                ? 'bg-green-600 text-white shadow-lg' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Flame className="h-4 w-4" />
            Bekleyen
            {signals.length > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                activeTab === 'active' ? 'bg-white/20' : 'bg-green-500/20 text-green-400'
              }`}>
                {signals.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('positions')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'positions' 
                ? 'bg-blue-600 text-white shadow-lg' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Target className="h-4 w-4" />
            Aktif
            {positions.length > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                activeTab === 'positions' ? 'bg-white/20' : 'bg-blue-500/20 text-blue-400'
              }`}>
                {positions.length}
              </span>
            )}
          </button>
          {/* Geçmiş butonu geçici olarak gizlendi - backend kayıtları devam ediyor */}
          {/* 
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'history' 
                ? 'bg-amber-600 text-white shadow-lg' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Geçmiş</span>
          </button>
          */}
        </div>

        {/* Tab Content */}
        {activeTab === 'active' && (
          <>
            {signals.length === 0 ? (
              <Card className="border-slate-700/50 bg-slate-900/50">
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                    <Timer className="h-8 w-8 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Şu An Bekleyen Sinyal Yok</h3>
                  <p className="text-slate-400 text-sm mb-4 max-w-sm mx-auto">
                    Piyasa koşulları sinyal için uygun değil. Bu bilinçli bir karar - zorlama sinyal vermiyoruz.
                  </p>
                  {/* Geçmişi Gör butonu geçici olarak gizlendi */}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {signals.length === 1 ? (
                  <SignalCard 
                    signal={signals[0]} 
                    isFeatured={true} 
                    livePrices={livePrices}
                    marketStatus={marketStatus}
                  />
                ) : (
                  <>
                    {bestSignal && (
                      <SignalCard 
                        signal={bestSignal} 
                        isFeatured={true}
                        livePrices={livePrices}
                        marketStatus={marketStatus}
                      />
                    )}
                    {signals.filter(s => s.id !== bestSignal?.id).map((signal) => (
                      <SignalCard 
                        key={signal.id} 
                        signal={signal}
                        livePrices={livePrices}
                        marketStatus={marketStatus}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Premium CTA Banner - After active signals, only for free users */}
        {activeTab === 'active' && !isPro && (
          <PremiumCTABanner onUpgrade={() => navigate('/pricing')} />
        )}

        {activeTab === 'history' && (
          <HistoryTab />
        )}

        {activeTab === 'positions' && (
          <PositionsTab 
            positions={positions}
            livePrices={livePrices}
            refreshing={refreshing}
            onRefresh={() => fetchPositions().then(setPositions)}
            onTabChange={setActiveTab}
          />
        )}

        {/* Premium CTA Banner - After positions, only for free users */}
        {activeTab === 'positions' && !isPro && (
          <PremiumCTABanner onUpgrade={() => navigate('/pricing')} />
        )}
      </div>
    </div>
  );
};

export default SimpleSignalsPage;
